import { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  Mail,
  Send,
  RefreshCcw,
  Search,
  Inbox,
  LogIn,
  LogOut,
  AlertCircle,
  X,
  CheckCircle2,
  Loader2,
  Reply,
  ReplyAll,
  Forward,
} from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { PageHeader, Spinner, Empty, Field } from "@/components/UI";

export default function Emails() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [query, setQuery] = useState("");
  const [activeMsg, setActiveMsg] = useState(null);
  const [activeBusy, setActiveBusy] = useState(false);
  // composeState = null (closed) OR { to, cc, subject, body, headline }
  const [composeState, setComposeState] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      setStatus(await api.googleStatus());
    } catch {
      setStatus({ configured: false, connected: false });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadInbox = useCallback(async () => {
    if (!status?.connected) return;
    setLoadingMsgs(true);
    try {
      const res = await api.listGmail({ q: query || undefined, max_results: 30 });
      setMessages(res.messages || []);
    } catch (e) {
      toast(e?.response?.data?.detail || "Failed to load inbox", "error");
    } finally {
      setLoadingMsgs(false);
    }
  }, [status?.connected, query, toast]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (status?.connected) loadInbox();
  }, [status?.connected, loadInbox]);

  // Handle the ?google=connected redirect back from OAuth
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("google") === "connected") {
      toast("Google account connected", "success");
      sp.delete("google");
      window.history.replaceState({}, "", window.location.pathname);
      loadStatus();
    } else if (sp.get("google") === "error") {
      const reason = sp.get("reason") || "OAuth flow failed.";
      toast(`Google connection failed: ${reason}`, "error");
      sp.delete("google");
      sp.delete("reason");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [toast, loadStatus]);

  const connect = async () => {
    try {
      const { authorization_url } = await api.googleLogin();
      window.location.href = authorization_url;
    } catch (e) {
      toast(e?.response?.data?.detail || "Failed to start OAuth", "error");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Google account?")) return;
    try {
      await api.googleDisconnect();
      toast("Google account disconnected", "success");
      setMessages([]);
      setActiveMsg(null);
      loadStatus();
    } catch (e) {
      toast(e?.response?.data?.detail || "Disconnect failed", "error");
    }
  };

  const openMessage = async (m) => {
    setActiveBusy(true);
    try {
      const full = await api.getGmail(m.id);
      setActiveMsg(full);
    } catch (e) {
      toast(e?.response?.data?.detail || "Failed to open message", "error");
    } finally {
      setActiveBusy(false);
    }
  };

  // ---- Not configured ----
  if (!loadingStatus && status && !status.configured) {
    return (
      <div data-testid="page-emails">
        <PageHeader icon={Mail} title="Emails" subtitle="Read and send emails through your Google account" />
        <div className="card card-padded text-center py-12">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-amber-600" size={22} />
          </div>
          <h3 className="font-bold text-lg mb-2">Gmail not yet configured</h3>
          <p className="text-sm muted max-w-md mx-auto">
            Google OAuth credentials are missing. Add <code>GOOGLE_CLIENT_ID</code> and{" "}
            <code>GOOGLE_CLIENT_SECRET</code> to <code>backend/.env</code> and restart the backend.
          </p>
        </div>
      </div>
    );
  }

  // ---- Not connected ----
  if (!loadingStatus && status && !status.connected) {
    return (
      <div data-testid="page-emails">
        <PageHeader icon={Mail} title="Emails" subtitle="Connect your Google account to read and send mail" />
        <div className="card card-padded text-center py-14 max-w-xl mx-auto">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
            style={{ background: "var(--green-50)", color: "var(--green-700)" }}
          >
            <Mail size={26} />
          </div>
          <h3 className="font-bold text-xl mb-2">Connect your Google account</h3>
          <p className="text-sm muted mb-6">
            One-time sign-in. We&apos;ll then load your inbox here and let you send quotations
            straight from Jero-One Workspace.
          </p>
          <button
            data-testid="connect-google"
            onClick={connect}
            className="btn btn-primary"
            style={{ paddingLeft: 18, paddingRight: 18 }}
          >
            <LogIn size={14} /> Connect Google
          </button>
          <div className="text-xs muted mt-6">
            Scopes: read inbox · send mail · access Drive folder
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-emails">
      <PageHeader
        icon={Mail}
        title="Emails"
        subtitle={
          status?.email
            ? `Connected as ${status.email}`
            : "Read and send emails through your Google account"
        }
        action={
          <div className="flex items-center gap-2">
            <span className="badge" style={{ background: "var(--green-50)", color: "var(--green-700)" }}>
              <CheckCircle2 size={12} /> Connected
            </span>
            <button
              data-testid="email-compose"
              onClick={() => setComposeState({ headline: "New message" })}
              className="btn btn-primary"
            >
              <Send size={14} /> Compose
            </button>
            <button
              data-testid="google-disconnect"
              onClick={disconnect}
              className="btn btn-ghost"
              title="Disconnect Google"
            >
              <LogOut size={14} />
            </button>
          </div>
        }
      />

      {/* Search + refresh */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-3 text-slate-400" />
          <input
            data-testid="email-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadInbox()}
            placeholder='Gmail search (e.g. is:unread, from:client@…)'
            className="input pl-9"
          />
        </div>
        <button
          data-testid="email-refresh"
          onClick={loadInbox}
          className="btn btn-ghost"
          disabled={loadingMsgs}
        >
          {loadingMsgs ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
          Refresh
        </button>
      </div>

      {/* Inbox + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div
          className={`card overflow-hidden lg:col-span-5 ${
            activeMsg || activeBusy ? "hidden lg:block" : ""
          }`}
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Inbox size={14} style={{ color: "var(--green-700)" }} />
              <span className="font-semibold text-sm">Inbox</span>
            </div>
            <span className="text-xs muted">{messages.length} messages</span>
          </div>
          {loadingMsgs ? (
            <div className="empty"><Spinner /> &nbsp;Loading inbox…</div>
          ) : messages.length === 0 ? (
            <Empty icon={Inbox} title="No messages" description="Try a different search or refresh." />
          ) : (
            <div className="divide-y divide-slate-100 lg:max-h-[640px] lg:overflow-y-auto">
              {messages.map((m) => (
                <button
                  key={m.id}
                  data-testid={`email-row-${m.id}`}
                  onClick={() => openMessage(m)}
                  type="button"
                  className={`w-full text-left p-3 hover:bg-slate-50 active:bg-slate-100 transition ${
                    activeMsg?.id === m.id ? "bg-[var(--green-50)]" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="text-xs font-semibold truncate" style={{ color: "var(--ink-700)" }}>
                      {(m.from || "").replace(/<.*>$/, "").trim() || "(unknown)"}
                    </span>
                    <span className="text-[10px] muted shrink-0">
                      {formatGmailDate(m.date)}
                    </span>
                  </div>
                  <div className="text-sm font-medium truncate">{m.subject || "(no subject)"}</div>
                  <div className="text-xs muted truncate mt-0.5">{m.snippet}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div
          className={`card lg:col-span-7 min-h-[300px] ${
            !activeMsg && !activeBusy ? "hidden lg:block" : ""
          }`}
        >
          {activeBusy ? (
            <div className="empty"><Spinner /> &nbsp;Loading message…</div>
          ) : !activeMsg ? (
            <Empty
              icon={Mail}
              title="Select a message"
              description="Tap any email to read it."
            />
          ) : (
            <div className="p-4 sm:p-5 space-y-3">
              {/* Mobile-only: back to inbox */}
              <button
                data-testid="email-back-to-inbox"
                type="button"
                onClick={() => setActiveMsg(null)}
                className="lg:hidden inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 -ml-1 mb-1"
              >
                <X size={14} /> Back to inbox
              </button>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-xs muted uppercase tracking-wider">Subject</div>
                  <div className="text-base font-bold break-words">{activeMsg.subject}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0 flex-wrap">
                  <button
                    onClick={() => setComposeState(buildReply(activeMsg, status?.email, false))}
                    className="btn btn-ghost btn-sm"
                    data-testid="reply-btn"
                    title="Reply"
                  >
                    <Reply size={14} /> Reply
                  </button>
                  <button
                    onClick={() => setComposeState(buildReply(activeMsg, status?.email, true))}
                    className="btn btn-ghost btn-sm"
                    data-testid="reply-all-btn"
                    title="Reply all"
                  >
                    <ReplyAll size={14} /> <span className="hidden sm:inline">Reply all</span><span className="sm:hidden">All</span>
                  </button>
                  <button
                    onClick={() => setComposeState(buildForward(activeMsg))}
                    className="btn btn-ghost btn-sm"
                    data-testid="forward-btn"
                    title="Forward"
                  >
                    <Forward size={14} /> Forward
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <Field label="From"><div className="text-sm break-words">{activeMsg.from}</div></Field>
                <Field label="Date"><div className="text-sm">{activeMsg.date}</div></Field>
                <Field label="To"><div className="text-sm break-words">{activeMsg.to}</div></Field>
                {activeMsg.cc && <Field label="Cc"><div className="text-sm break-words">{activeMsg.cc}</div></Field>}
              </div>
              <div className="border-t border-slate-100 pt-4 overflow-x-auto">
                {/(<html|<body|<div|<p|<table)/i.test(activeMsg.body) ? (
                  <div
                    className="prose prose-sm max-w-none break-words"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(activeMsg.body, {
                        FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
                        FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
                      }),
                    }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap break-words text-sm font-sans">{activeMsg.body}</pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {composeState && (
        <ComposeModal
          initial={composeState}
          onClose={() => setComposeState(null)}
          onSent={() => {
            setComposeState(null);
            loadInbox();
          }}
        />
      )}
    </div>
  );
}

function formatGmailDate(d) {
  if (!d) return "";
  const t = new Date(d);
  if (isNaN(t)) return d.slice(0, 16);
  const now = new Date();
  const sameDay = t.toDateString() === now.toDateString();
  return sameDay
    ? t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : t.toLocaleDateString([], { month: "short", day: "numeric" });
}

// Extract just the email portion from a header value like:
//   "Jane Doe <jane@example.com>"  or  "jane@example.com"
function extractEmail(header) {
  if (!header) return "";
  const m = header.match(/<([^>]+)>/);
  return (m ? m[1] : header).trim().toLowerCase();
}

function splitAddresses(header) {
  if (!header) return [];
  return header
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function quoteBody(msg) {
  const dateLine = msg.date ? new Date(msg.date).toString() : "";
  const from = msg.from || "";
  const stripped = /(<html|<body|<div|<p|<table)/i.test(msg.body || "")
    ? (msg.body || "").replace(/<[^>]+>/g, "").replace(/\n{3,}/g, "\n\n")
    : msg.body || "";
  const quoted = stripped
    .split("\n")
    .map((l) => "> " + l)
    .join("\n");
  return `\n\nOn ${dateLine}, ${from} wrote:\n${quoted}\n`;
}

function buildReply(msg, myEmail, replyAll) {
  const me = (myEmail || "").toLowerCase();
  const originalFrom = msg.from || "";
  const to = extractEmail(originalFrom);
  let cc = "";
  if (replyAll) {
    const others = [
      ...splitAddresses(msg.to || ""),
      ...splitAddresses(msg.cc || ""),
    ]
      .filter((addr) => {
        const e = extractEmail(addr);
        return e && e !== me && e !== to;
      });
    // De-dupe by email
    const seen = new Set();
    cc = others
      .filter((a) => {
        const e = extractEmail(a);
        if (seen.has(e)) return false;
        seen.add(e);
        return true;
      })
      .join(", ");
  }
  const subj = /^re:/i.test(msg.subject || "")
    ? msg.subject
    : `Re: ${msg.subject || ""}`;
  return {
    headline: replyAll ? "Reply all" : "Reply",
    to,
    cc,
    subject: subj,
    body: quoteBody(msg),
    // RFC 2822 threading hints — passed through to Gmail
    in_reply_to: msg.message_id_header || "",
    references: msg.references || "",
    thread_id: msg.thread_id || "",
  };
}

function buildForward(msg) {
  const subj = /^fwd:/i.test(msg.subject || "")
    ? msg.subject
    : `Fwd: ${msg.subject || ""}`;
  const header =
    `\n\n---------- Forwarded message ----------\n` +
    `From: ${msg.from || ""}\n` +
    `Date: ${msg.date || ""}\n` +
    `Subject: ${msg.subject || ""}\n` +
    `To: ${msg.to || ""}\n` +
    (msg.cc ? `Cc: ${msg.cc}\n` : "") +
    `\n${/(<html|<body|<div|<p|<table)/i.test(msg.body || "")
      ? (msg.body || "").replace(/<[^>]+>/g, "").replace(/\n{3,}/g, "\n\n")
      : msg.body || ""}\n`;
  return {
    headline: "Forward",
    to: "",
    cc: "",
    subject: subj,
    body: header,
    // Forwards intentionally do NOT set thread_id — Gmail should start a new thread
  };
}

function ComposeModal({ initial, onClose, onSent }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    to: initial?.to || "",
    cc: initial?.cc || "",
    subject: initial?.subject || "",
    body: initial?.body || "",
  });
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!form.to || !form.subject) {
      toast("To and Subject are required", "error");
      return;
    }
    setSending(true);
    try {
      await api.sendGmail({
        ...form,
        // RFC 2822 threading — only sent for replies (forward drops thread_id)
        in_reply_to: initial?.in_reply_to || "",
        references: initial?.references || "",
        thread_id: initial?.thread_id || "",
      });
      toast("Email sent", "success");
      onSent();
    } catch (e) {
      toast(e?.response?.data?.detail || "Send failed", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="compose-modal"
      className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white w-full lg:max-w-2xl rounded-t-2xl lg:rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="font-bold text-base">{initial?.headline || "New message"}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="To *">
            <input
              data-testid="compose-to"
              className="input"
              value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              placeholder="recipient@example.com"
            />
          </Field>
          <Field label="Cc">
            <input
              className="input"
              value={form.cc}
              onChange={(e) => setForm({ ...form, cc: e.target.value })}
            />
          </Field>
          <Field label="Subject *">
            <input
              data-testid="compose-subject"
              className="input"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
          </Field>
          <Field label="Message">
            <textarea
              data-testid="compose-body"
              rows={9}
              className="textarea"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write your message…"
            />
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn btn-ghost">Cancel</button>
          <button
            data-testid="compose-send"
            onClick={send}
            disabled={sending}
            className="btn btn-primary"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
